import { useCallback, useRef, useState } from 'react';
import {
  IBulkUpdateRow,
  IBulkUpdateRowError,
  validateBulkUpdateFile,
  validateBulkUpdateSheet,
} from '../../../utils/bulkUpdateValidation';
import { IBulkUpdateUploadResponse, uploadBulkUpdateFile } from '../../../services/bulkUpdate';

export type TBulkUpdateStatus = 'idle' | 'validating' | 'invalid' | 'ready' | 'submitting' | 'done';

const PARSE_ERROR_MESSAGE = 'The file could not be read. Please make sure it is a valid .xlsx file.';
const STALE_FILE_MESSAGE =
  'The file is no longer available. It may have been moved, renamed or edited since you selected it.';

// The browser keeps a handle to the file on disk, so re-read a byte to confirm it still exists.
const assertFileStillReadable = async (file: File): Promise<void> => {
  try {
    await file.slice(0, 1).arrayBuffer();
  } catch (error) {
    throw new Error(STALE_FILE_MESSAGE);
  }
};

// The parser only reads the first sheet, so say so when that sheet turns out to be the wrong one.
const appendSheetHint = (fatalError: string | null, sheetNames: string[]): string | null => {
  if (!fatalError || sheetNames.length < 2) {
    return fatalError;
  }
  return `${fatalError} Only the first sheet ("${sheetNames[0]}") is read.`;
};

export const useBulkUpdateUpload = () => {
  const [status, setStatus] = useState<TBulkUpdateStatus>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<IBulkUpdateRow[]>([]);
  const [errors, setErrors] = useState<IBulkUpdateRowError[]>([]);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [result, setResult] = useState<IBulkUpdateUploadResponse | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
    setFile(null);
    setRows([]);
    setErrors([]);
    setFatalError(null);
    setResult(null);
  }, []);

  const selectFile = useCallback(async (selected: File) => {
    setFile(selected);
    setRows([]);
    setErrors([]);
    setResult(null);

    const fileError = validateBulkUpdateFile(selected);
    if (fileError) {
      setFatalError(fileError);
      setStatus('invalid');
      return;
    }

    setFatalError(null);
    setStatus('validating');

    let sheet: unknown[][];
    let sheetNames: string[] = [];
    try {
      const excel = await import('read-excel-file');
      sheetNames = await excel.readSheetNames(selected);
      sheet = (await excel.default(selected)) as unknown[][];
    } catch (error) {
      setFatalError(PARSE_ERROR_MESSAGE);
      setStatus('invalid');
      return;
    }

    const validation = validateBulkUpdateSheet(sheet);
    setFatalError(appendSheetHint(validation.fatalError, sheetNames));
    setRows(validation.rows);
    setErrors(validation.errors);
    setStatus(validation.fatalError || validation.errors.length > 0 ? 'invalid' : 'ready');
  }, []);

  const submit = useCallback(async () => {
    if (!file) {
      return undefined;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('submitting');
    try {
      await assertFileStillReadable(file);
      const response = await uploadBulkUpdateFile(file, controller.signal);
      setResult(response);
      setStatus('done');
      return response;
    } catch (error) {
      // reset() already cleared the state when the upload was aborted.
      if (!controller.signal.aborted) {
        setStatus('ready');
      }
      throw error;
    } finally {
      abortRef.current = null;
    }
  }, [file]);

  return { status, file, rows, errors, fatalError, result, selectFile, submit, reset };
};
